import { MUISelect, MUITextField } from "@abb/arcadia-ui-react"
import { zodResolver } from "@hookform/resolvers/zod"
import { StatusCodes } from "http-status-codes"
import { useCallback, useEffect, useState } from "react"
import { Controller, ControllerRenderProps, useForm, useWatch } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
import { z } from "zod"
import { useApplicationPermissions } from "../../../../../../hooks/useApplicationPermissions"
import { useApplications } from "../../../../../../hooks/useApplications"
import { digitalManagerApi } from "../../../../../../services/api"
import { FieldRow, FormPage } from "../../../../../components/FormPage"
import { ApplicationPermissionGet } from "../../../../../models/ApplicationPermission"
import {
  AccessLevel,
  DimaFeatureShortNameEnum,
} from "../../../../../models/Authorization"
import { Guid } from "../../../../../models/CustomType"
import { DeviceRolePermissionGet } from "../../../../../models/DeviceRolePermission"
import { ControllerRendererProps } from "../../../../../types/reactHookForm"

const validationSchema = z.object({
  permissionId: z.string().min(1, "Permission is required"),
  applicationId: z.string().min(1, "Permission is required"),
})

export function DeviceRolePermissionCreate() {
  const { deviceId, roleId } = useParams()
  const [deviceRolePermissionsIds, setDeviceRolePermissionsIds] = useState<Guid[]>([])
  const { applicationsList, isLoadingApplications } = useApplications(
    DimaFeatureShortNameEnum.AM,
    AccessLevel.Write
  )
  const navigate = useNavigate()
  const returnURL = `/devices/${deviceId}/roles/${roleId}#permissions`

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<{ permissionId: string; applicationId: string }>({
    resolver: zodResolver(validationSchema),
    mode: "onChange",
  })

  useEffect(() => {
    reset({
      permissionId: "",
      applicationId: "",
    })
  }, [reset])

  const onSubmit = async (values: {
    permissionId: string
    applicationId: string
  }) => {
    if (!values.permissionId) {
      return
    }

    await digitalManagerApi
      .post(`/devices/${deviceId}/roles/${roleId}/permissions/${values.permissionId}`)
      .then((res) => {
        if (res.status === StatusCodes.CREATED) {
          toast.success("Permission added to role")
          navigate(returnURL)
        }
      })
      .catch(() => {
        toast.error("Cannot add permission to role")
      })
  }

  useEffect(() => {
    if (deviceId && roleId) {
      digitalManagerApi
        .get<
          DeviceRolePermissionGet[]
        >(`/devices/${deviceId}/roles/${roleId}/permissions`)
        .then((res) => {
          if (res.status === StatusCodes.OK && res.data?.length) {
            setDeviceRolePermissionsIds(
              res.data.map(
                (deviceRolePermission: DeviceRolePermissionGet) =>
                  deviceRolePermission.permission.id
              )
            )
          }
        })
    }
  }, [deviceId, roleId])

  //#region Application
  const handleApplicationIdSelectChange = useCallback(
    (
      field: ControllerRenderProps<
        { permissionId: string; applicationId: string },
        "applicationId"
      >
    ) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )
  const renderApplicationIdSelect = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { permissionId: string; applicationId: string },
      "applicationId"
    >) => (
      <MUISelect
        name={field.name}
        id="applicationId"
        size={32}
        dropdownLOV={applicationsList.map((a) => ({
          id: String(a.id),
          name: a.name ?? "",
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleApplicationIdSelectChange(field)}
        onBlur={field.onBlur}
        isDisabled={isLoadingApplications}
        errors={
          errors.applicationId?.message
            ? { applicationId: { message: String(errors.applicationId.message) } }
            : undefined
        }
      />
    ),
    [
      applicationsList,
      handleApplicationIdSelectChange,
      isLoadingApplications,
      errors.applicationId,
    ]
  )
  //#endregion

  //#region Permission
  const selectedApplicationId = useWatch({
    control,
    name: "applicationId",
  })

  const { applicationPermissions, isLoadingApplicationPermissions } =
    useApplicationPermissions(selectedApplicationId)

  const handleAutocompleteGetOptionLabel = useCallback(
    (applicationPermission: ApplicationPermissionGet) => applicationPermission.name,
    []
  )

  const handleAutocompleteGetOptionDisabled = useCallback(
    (option: ApplicationPermissionGet) => {
      const optionAlreadySelected = deviceRolePermissionsIds.find(
        (deviceRolePermissionId: Guid) => option.id === deviceRolePermissionId
      )
      return !!optionAlreadySelected
    },
    [deviceRolePermissionsIds]
  )

  const handleChangeValues = useCallback(
    (next: Array<string | { id: string }>) => {
      const firstItem = next?.[0]
      const selectedId = typeof firstItem === "string" ? firstItem : firstItem?.id
      setValue("permissionId", selectedId ?? "", {
        shouldValidate: true,
        shouldDirty: true,
      })
    },
    [setValue]
  )

  const renderPermissionAutocomplete = useCallback(
    ({
      field,
    }: ControllerRendererProps<
      { permissionId: string; applicationId: string },
      "permissionId"
    >) => (
      <MUISelect
        id="permissionId"
        name="permissionId"
        placeholder="Select permission"
        size={32}
        dropdownLOV={(applicationPermissions ?? []).map((opt) => ({
          id: opt.id,
          name: handleAutocompleteGetOptionLabel(opt) ?? "",
          isDisabled: handleAutocompleteGetOptionDisabled(opt),
        }))}
        enableSearch
        clearable={false}
        isDisabled={!selectedApplicationId || isLoadingApplicationPermissions}
        selectedItem={field.value ? [field.value] : []}
        onChange={handleChangeValues}
        errors={
          typeof errors.permissionId?.message === "string"
            ? { permissionId: { message: errors.permissionId.message } }
            : undefined
        }
      />
    ),
    [
      applicationPermissions,
      handleAutocompleteGetOptionDisabled,
      handleAutocompleteGetOptionLabel,
      handleChangeValues,
      selectedApplicationId,
      isLoadingApplicationPermissions,
      errors.permissionId?.message,
    ]
  )
  //#endregion

  return (
    <FormPage
      returnURL={returnURL}
      onSubmit={handleSubmit(onSubmit)}
      isSubmitting={isSubmitting}
      isDirty={isDirty}
      isValid={isValid}
    >
      <FieldRow label="Application">
        <>
          <Controller
            name="applicationId"
            control={control}
            render={renderApplicationIdSelect}
          />
          {errors.applicationId && (
            <MUITextField
              isDisabled
              value=""
              errors={{
                applicationId: {
                  message:
                    typeof errors.applicationId?.message === "string"
                      ? errors.applicationId.message
                      : "",
                },
              }}
            />
          )}
        </>
      </FieldRow>
      <FieldRow label="Permission">
        <Controller
          name="permissionId"
          control={control}
          render={renderPermissionAutocomplete}
        />
      </FieldRow>
    </FormPage>
  )
}
